import { useMemo } from "react";
import { countsAsDismissed } from "@/lib/outOfScope";
import { BRIDGES, angleScore, isAngleBridge, isAngleMember, validAngle } from "@/lib/angles";
import type { ConceptScope, FacetSummary } from "@/types/concept";
import type { PaperWithRole } from "@/types/paper";
import type { DebateMap } from "@/hooks/useFieldDebate";
import { useEvidenceStore } from "@/stores/evidence-store";

/** Filter a paper list down to those whose id is in `scope`. Pure helper
 * exported for direct testing. Returns input unchanged when scope is
 * undefined; returns an empty array when scope is an empty list. */
export function applyPaperIdScope<T extends { id: string }>(
  papers: T[],
  scope: string[] | undefined,
): T[] {
  if (scope === undefined) return papers;
  const set = new Set(scope);
  return papers.filter((p) => set.has(p.id));
}

/** Facets 1c-2: active angles (position order) + concept scope for the angle tab. */
export interface AngleContext { facets: FacetSummary[]; conceptScope: ConceptScope | null | undefined }

export interface PaperFilters {
  searchText?: string;
  minCitations?: number;
  earliestYear?: number;
  isInfluential?: boolean;
  minScore?: number;
  contributionType?: string;
  argumentationRole?: string;

  overrideFilter?: "active" | "championed" | "dismissed" | "none";
  readingStatus?: "unread" | "skimmed" | "read";
  tagIds?: string[];
  showDismissed?: boolean;
  showFlagged?: boolean;
  flaggedView?: boolean;
  dismissedView?: boolean;
  sourceFilter?: "monitored" | "trawled" | "imported";
  addedWithin?: "24h" | "7d" | "30d";
  monitorInboxView?: boolean;
  /** Only papers the user has attached ClaimEvidence to (Curate Evidence tier). */
  hasEvidence?: boolean;
  /** Only papers with clips in the session's pending-evidence stash
   *  (evidence-store — same list as the Evidence Map tray). */
  hasStashed?: boolean;
  /** Only papers with a discovered_via link from this seed's paper id. */
  viaSeed?: string;
  /** Facets 1c-2: active facet id (angle tab) or "bridges". Curate URL only —
   *  never mirrored to the filter store. Ignored without a valid AngleContext. */
  angle?: string;
}

export type SortField =
  | "title"
  | "year"
  | "citations"
  | "relevance_score"
  | "reading_priority"
  | "tension"
  | "added_at";
export type SortDirection = "asc" | "desc";

const READING_WEIGHT: Record<string, number> = {
  unread: 1.0,
  skimmed: 0.5,
  read: 0.1,
};

export function computeReadingPriority(paper: PaperWithRole): number {
  const effectiveScore = paper.user_score_override ?? paper.normalized_score;
  const readingWeight = READING_WEIGHT[paper.reading_status] ?? 1.0;
  const contentBonus = paper.full_text_status === "fetched" ? 5 : 0;
  return Math.max(0, Math.min(100, effectiveScore * readingWeight + contentBonus));
}

function computeTensionScore(paper: PaperWithRole, debateMap?: DebateMap): number {
  if (paper.is_bridge) return 100;

  const effectiveScore = paper.user_score_override ?? paper.normalized_score;
  if (paper.argumentation_role === "challenges" && effectiveScore >= 70) return 80;

  if (debateMap) {
    const campCount = debateMap.camps.filter((c) =>
      c.paper_ids.includes(paper.id),
    ).length;
    if (campCount >= 2) return 60;
  }

  if (paper.argumentation_role === "challenges") return 40;

  return 0;
}

export function useFilteredPapers(
  papers: PaperWithRole[],
  filters: PaperFilters,
  sortField?: SortField,
  sortDirection?: SortDirection,
  debateMap?: DebateMap,
  paperIdScope?: string[],
  angleCtx?: AngleContext,
) {
  const pendingEvidence = useEvidenceStore((s) => s.pendingEvidence);

  const filtered = useMemo(() => {
    const angle = angleCtx ? validAngle(filters.angle, angleCtx.facets) : undefined;
    let result = applyPaperIdScope(papers, paperIdScope);

    if (filters.hasStashed) {
      const stashedPaperIds = new Set(pendingEvidence.map((e) => e.paperId));
      result = result.filter((p) => stashedPaperIds.has(p.id));
    }

    // Dismissed view: show ONLY dismissed papers. Default: exclude dismissed.
    if (filters.dismissedView) {
      result = result.filter(countsAsDismissed);
    } else if (!filters.showDismissed) {
      result = result.filter((p) => !p.exclusion_reason);
    }

    // Flagged view: show ONLY flagged papers. Default: exclude flagged.
    if (filters.flaggedView) {
      result = result.filter((p) => !!p.flagged_for_inbox);
    } else if (!filters.showFlagged) {
      result = result.filter((p) => !p.flagged_for_inbox);
    }

    if (filters.searchText) {
      const term = filters.searchText.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(term) ||
          p.authors.some((a) => a.toLowerCase().includes(term)) ||
          p.journal.toLowerCase().includes(term) ||
          (p.notes && p.notes.toLowerCase().includes(term)),
      );
    }

    if (filters.minCitations != null && filters.minCitations > 0) {
      result = result.filter((p) => p.citations >= filters.minCitations!);
    }

    if (filters.earliestYear != null) {
      result = result.filter(
        (p) => p.year != null && p.year >= filters.earliestYear!,
      );
    }

    if (filters.isInfluential) {
      result = result.filter((p) => p.is_influential);
    }

    if (filters.minScore != null && filters.minScore > 0) {
      // Skip minScore when explicitly viewing overridden papers
      if (filters.overrideFilter !== "championed" && filters.overrideFilter !== "dismissed") {
        result = result.filter((p) => {
          const effective = p.user_score_override ?? p.normalized_score;
          return effective >= filters.minScore!;
        });
      }
    }

    if (filters.contributionType) {
      const ct = filters.contributionType.toLowerCase();
      result = result.filter(
        (p) => p.contribution_type.toLowerCase() === ct,
      );
    }

    if (filters.argumentationRole) {
      const ar = filters.argumentationRole.toLowerCase();
      result = result.filter(
        (p) => p.argumentation_role.toLowerCase() === ar,
      );
    }

    if (filters.overrideFilter) {
      if (filters.overrideFilter === "active") {
        // Exclude dismissed papers (override <= 30)
        result = result.filter((p) => !(p.user_score_override != null && p.user_score_override <= 30));
      } else if (filters.overrideFilter === "championed") {
        result = result.filter((p) => p.user_score_override != null && p.user_score_override >= 70);
      } else if (filters.overrideFilter === "dismissed") {
        result = result.filter((p) => p.user_score_override != null && p.user_score_override <= 30);
      } else {
        // "none" — no override set
        result = result.filter((p) => p.user_score_override == null);
      }
    }

    if (filters.readingStatus) {
      result = result.filter(
        (p) => p.reading_status === filters.readingStatus,
      );
    }

    if (filters.hasEvidence) {
      result = result.filter((p) => p.has_evidence);
    }

    if (filters.tagIds && filters.tagIds.length > 0) {
      const tagSet = new Set(filters.tagIds);
      result = result.filter(
        (p) => p.tags?.some((t) => tagSet.has(t.id)),
      );
    }

    // Source filter
    if (filters.sourceFilter) {
      const sourceMap: Record<string, string[]> = {
        monitored: ["monitor", "trawl_references"],
        trawled: ["trawl", "smart_trawl"],
        imported: ["bibtex_import", "pdf_import", "zotero_import", "inbox"],
      };
      const allowed = sourceMap[filters.sourceFilter];
      if (allowed) {
        result = result.filter((p) => allowed.includes(p.added_source));
      }
    }

    if (filters.viaSeed) {
      const seed = filters.viaSeed;
      result = result.filter((p) => p.discovered_via?.some((l) => l.parent_paper_id === seed) ?? false);
    }

    // Recency filter
    if (filters.addedWithin) {
      const msMap: Record<string, number> = {
        "24h": 24 * 60 * 60 * 1000,
        "7d": 7 * 24 * 60 * 60 * 1000,
        "30d": 30 * 24 * 60 * 60 * 1000,
      };
      const cutoff = Date.now() - (msMap[filters.addedWithin] ?? 0);
      result = result.filter(
        (p) => p.added_at && new Date(p.added_at).getTime() >= cutoff,
      );
    }

    if (angle && angleCtx) {
      const { facets, conceptScope } = angleCtx;
      if (angle === BRIDGES) {
        result = result.filter((p) => isAngleBridge(p, facets, conceptScope));
      } else {
        const facet = facets.find((f) => f.id === angle)!;
        result = result.filter((p) => isAngleMember(p, facet, conceptScope));
      }
    }

    // Sort
    if (sortField) {
      const dir = sortDirection === "asc" ? 1 : -1;
      result = [...result].sort((a, b) => {
        let av: number | string;
        let bv: number | string;
        if (sortField === "tension") {
          av = computeTensionScore(a, debateMap);
          bv = computeTensionScore(b, debateMap);
        } else if (sortField === "reading_priority") {
          av = computeReadingPriority(a);
          bv = computeReadingPriority(b);
        } else if (sortField === "relevance_score") {
          // Angle tab: rank by that angle; ties fall back to the overall.
          const byAngle = angle && angle !== BRIDGES;
          av = (byAngle ? (angleScore(a, angle) ?? 0) * 1000 : 0) + (a.user_score_override ?? a.normalized_score ?? 0);
          bv = (byAngle ? (angleScore(b, angle) ?? 0) * 1000 : 0) + (b.user_score_override ?? b.normalized_score ?? 0);
        } else if (sortField === "added_at") {
          av = a.added_at ? new Date(a.added_at).getTime() : 0;
          bv = b.added_at ? new Date(b.added_at).getTime() : 0;
        } else {
          av = a[sortField] ?? 0;
          bv = b[sortField] ?? 0;
        }
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }

    return result;
  }, [papers, filters, sortField, sortDirection, debateMap, paperIdScope, pendingEvidence, angleCtx]);

  return { filtered, total: papers.length };
}
